import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface Dashboard {
  id: string
  name: string
  department: string
  is_public: boolean
  widget_count: number
  view_count: number
  last_viewed: string
  is_favorite: boolean
}

interface KPI {
  id: string
  name: string
  category: string
  target: number
  actual: number
  unit: string
  status: string
  trend: string
}

export default function BIPage() {
  const [dashboards, setDashboards] = useState<Dashboard[]>([])
  const [kpis, setKPIs] = useState<KPI[]>([])
  const [statistics, setStatistics] = useState<any>(null)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [dashRes, kpiRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/bi/dashboards'),
      fetch(API_BASE + '/bi/kpis'),
      fetch(API_BASE + '/bi/statistics')
    ])
    if (dashRes.ok) setDashboards((await dashRes.json()).dashboards || [])
    if (kpiRes.ok) setKPIs((await kpiRes.json()).kpis || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'on_track': return 'bg-green-100 text-green-800'
      case 'at_risk': return 'bg-yellow-100 text-yellow-800'
      case 'off_track': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'up': return '↑'
      case 'down': return '↓'
      default: return '→'
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">Business Intelligence</h1>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">+ New Dashboard</button>
        </div>

        {statistics && (
          <div className="grid grid-cols-5 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Dashboards</div>
              <div className="text-2xl font-bold">{statistics.total_dashboards}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total KPIs</div>
              <div className="text-2xl font-bold text-blue-600">{statistics.total_kpis}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">KPIs On Track</div>
              <div className="text-2xl font-bold text-green-600">{statistics.kpis_on_track}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">KPIs At Risk</div>
              <div className="text-2xl font-bold text-yellow-600">{statistics.kpis_at_risk}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Active Users</div>
              <div className="text-2xl font-bold text-purple-600">{statistics.active_users}</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">KPI Overview</h2></div>
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">KPI</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Target</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actual</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {kpis.slice(0, 12).map(kpi => (
                  <tr key={kpi.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="font-medium text-sm">{kpi.name}</div>
                      <div className="text-xs text-gray-500 capitalize">{kpi.category}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm">{kpi.target} {kpi.unit}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium">{kpi.actual} {kpi.unit}</div>
                      <div className={`text-xs ${kpi.trend === 'up' ? 'text-green-600' : kpi.trend === 'down' ? 'text-red-600' : 'text-gray-600'}`}>
                        {getTrendIcon(kpi.trend)} {kpi.trend}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded text-xs ${getStatusColor(kpi.status)}`}>{kpi.status.replace('_', ' ')}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Dashboards</h3>
            <div className="space-y-3">
              {dashboards.slice(0, 8).map(dashboard => (
                <div key={dashboard.id} className="p-3 bg-gray-50 rounded hover:bg-gray-100 cursor-pointer">
                  <div className="flex justify-between items-start mb-1">
                    <span className="font-medium text-sm">{dashboard.name}</span>
                    {dashboard.is_favorite && <span className="text-yellow-500">★</span>}
                  </div>
                  <div className="text-xs text-gray-500">{dashboard.department}</div>
                  <div className="flex justify-between items-center mt-2">
                    <span className="text-xs text-gray-600">{dashboard.widget_count} widgets</span>
                    <span className="text-xs text-gray-500">{dashboard.view_count} views</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
